"use client";

import { useState, useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ArrowRight, ChevronLeft } from "lucide-react";
import Link from "next/link";

const MIN_ORDERS = 20;
const MAX_ORDERS = 600;
const DEFAULT_ORDERS = 200;
const AVG_TICKET = 30; // USD
const DOORDASH_TAKE_RATE = 0.30; // EXAMPLE: marketplace apps can take up to 30% — labeled as an example in the copy

function useOdometer(target: number, durationMs = 700) {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const start = display;
    const delta = target - start;
    const startedAt = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startedAt;
      const t = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(start + delta * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs]);

  return display;
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

export default function OnlineOrderingHero() {
  const { t } = useLang();
  const [orders, setOrders] = useState(DEFAULT_ORDERS);
  const monthlyVolume = orders * AVG_TICKET;
  const doordashTake = monthlyVolume * DOORDASH_TAKE_RATE;
  const savings = doordashTake; // Redy = $0

  const doordashDisplay = useOdometer(doordashTake);
  const savingsDisplay = useOdometer(savings);
  const settleControls = useAnimation();

  const handleRelease = () => {
    settleControls.start({
      scale: [1, 1.04, 1],
      transition: { duration: 0.45, ease: [0.21, 0.47, 0.32, 0.98] },
    });
  };

  return (
    <section className="relative pt-32 pb-24 px-6 overflow-hidden bg-[#FAFAF8]">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/feature-online-ordering.mp4" type="video/mp4" />
      </video>
      {/* Light overlay — video shows through */}
      <div className="absolute inset-0 bg-[#FAFAF8]/80 pointer-events-none" />
      <div
        className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.06) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-redy-secondary hover:text-redy-charcoal transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#D7263D" }}
        >
          {t("Direct ordering · No middleman", "Pedidos directos · Sin intermediario")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[44px] sm:text-6xl md:text-[88px] lg:text-[110px] font-black leading-[0.95] tracking-[-0.04em] mb-6 text-redy-charcoal"
        >
          {t("They already", "Ya viven")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("live down the street.", "en tu cuadra.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-redy-secondary max-w-[640px] mx-auto leading-relaxed mb-16"
        >
          {t(
            "Why pay a marketplace up to 30% for a customer who already lives down the street? Take orders from your own site. Keep the commission.",
            "¿Por qué pagarle hasta 30% a un marketplace por un cliente que ya vive en tu cuadra? Recibe pedidos desde tu propio sitio. Quédate con la comisión."
          )}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="relative max-w-[920px] mx-auto"
        >
          <div
            className="absolute -inset-4 rounded-[40px] pointer-events-none"
            style={{ background: "radial-gradient(ellipse at center, rgba(244,114,43,0.12) 0%, transparent 65%)" }}
          />

          <motion.div
            animate={settleControls}
            className="relative bg-white rounded-[32px] border border-black/5 shadow-[0_30px_80px_rgba(0,0,0,0.06)] p-8 md:p-12"
          >
            <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-2">
              {t("Your monthly online orders", "Tus pedidos online al mes")}
            </div>

            <div className="flex items-end justify-between mb-3">
              <div className="text-[42px] md:text-[56px] font-black tracking-tight text-redy-charcoal leading-none tabular-nums">
                {orders.toLocaleString()}
              </div>
              <div className="text-[12px] font-bold text-redy-secondary mb-2">
                {t("orders", "pedidos")} · ${AVG_TICKET} {t("avg", "prom")}
              </div>
            </div>

            <input
              type="range"
              min={MIN_ORDERS}
              max={MAX_ORDERS}
              step={5}
              value={orders}
              onChange={(e) => setOrders(Number(e.target.value))}
              onMouseUp={handleRelease}
              onTouchEnd={handleRelease}
              className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gradient-to-r from-[#F4722B] to-[#D7263D]/30 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-[#F4722B] [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-grab active:[&::-webkit-slider-thumb]:cursor-grabbing"
            />

            <div className="flex justify-between text-[11px] font-bold text-redy-secondary/50 mt-2">
              <span>{MIN_ORDERS}</span>
              <span>{MAX_ORDERS}+</span>
            </div>

            {/* Volume sub-line */}
            <div className="mt-4 text-[12px] font-medium text-redy-secondary/70">
              {t("That's", "Eso es")}{" "}
              <span className="font-bold text-redy-charcoal tabular-nums">
                {formatCurrency(monthlyVolume)}
              </span>{" "}
              {t("in online sales per month.", "en ventas online al mes.")}
            </div>

            <div className="h-px bg-black/5 my-10" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 mb-8">
              <div className="text-left">
                <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
                  {t("A marketplace can take", "Un marketplace puede llevarse")}
                </div>
                <div className="text-[40px] md:text-[52px] font-black tracking-tight text-redy-charcoal/40 leading-none tabular-nums">
                  −{formatCurrency(doordashDisplay)}
                </div>
                <div className="text-[12px] font-medium text-redy-secondary/70 mt-2">
                  {t("Up to 30% in commission and fees (example).", "Hasta 30% en comisión y fees (ejemplo).")}
                </div>
              </div>

              <div className="text-left relative">
                <div
                  className="absolute -inset-x-4 -inset-y-3 rounded-[20px] pointer-events-none"
                  style={{ background: "radial-gradient(ellipse, rgba(244,114,43,0.08) 0%, transparent 70%)" }}
                />
                <div className="relative">
                  <div className="text-[10px] font-black tracking-widest uppercase mb-3" style={{ color: "#D7263D" }}>
                    {t("Redy takes", "Redy se lleva")}
                  </div>
                  <div className="text-[40px] md:text-[52px] font-black tracking-tight leading-none tabular-nums" style={{ color: "#D7263D" }}>
                    $0
                  </div>
                  <div className="text-[12px] font-medium mt-2" style={{ color: "#D7263D", opacity: 0.7 }}>
                    {t("0% platform fee on online orders.", "0% de fee de plataforma en pedidos online.")}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#F4722B] to-[#D7263D] rounded-[24px] px-6 py-6 md:px-8 md:py-8 text-white shadow-lg shadow-[#F4722B]/20">
              <div className="flex items-start md:items-center justify-between gap-4 flex-col md:flex-row">
                <div>
                  <div className="text-[11px] font-black tracking-widest uppercase text-white/70 mb-2">
                    {t("Back in your pocket", "De vuelta a tu bolsillo")}
                  </div>
                  <div className="text-[48px] md:text-[64px] font-black leading-none tabular-nums">
                    +{formatCurrency(savingsDisplay)}
                  </div>
                  <div className="text-[13px] font-bold text-white/85 mt-1">
                    /{t("month", "mes")} · {formatCurrency(savingsDisplay * 12)}/{t("year", "año")}
                  </div>
                </div>
                <Link
                  href="/signup"
                  className="inline-flex items-center gap-2 bg-white text-redy-charcoal font-bold text-[14px] px-6 py-3.5 rounded-full hover:scale-[1.03] transition-transform shadow-lg whitespace-nowrap"
                >
                  {t("Stop paying commission", "Deja de pagar comisión")}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-8 text-[12px] font-medium text-redy-secondary/60"
        >
          {t(
            "Example at a 30% take rate. Marketplace apps can take up to 30% in commission and fees; real rates vary by app and plan.",
            "Ejemplo con una tasa de 30%. Las apps de marketplace pueden cobrar hasta 30% en comisión y fees; la tasa real varía según la app y el plan."
          )}
        </motion.div>
      </div>
    </section>
  );
}
